import React, { useState, useEffect } from "react";
import {
  ShoppingCart,
  User,
  Menu,
  Globe,
  ShieldCheck,
  Truck,
  PhoneCall,
} from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "./AuthProvider";
import { useCart } from "./CartProvider";
import { LANGUAGES } from "../lib/constants";
import logoSvg from "@/assets/images/logo.svg";

export function Header({
  autoHide = false,
}: {
  autoHide?: boolean; // Option to enable/disable auto-hide on scroll
}) {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const { getItemCount, setIsOpen: setCartOpen } = useCart();
  const { theme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const scrollThreshold = 10;
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();

  const itemCount = getItemCount();
  const currentLanguage =
    LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  const changeLanguage = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    // Get the current path without the language prefix
    const pathParts = location.pathname.split("/").filter(Boolean);
    // Remove the first part only if it is a recognised language code (not just any 2-char segment)
    const supportedCodes = LANGUAGES.map((l) => l.code);
    if (pathParts.length > 0 && supportedCodes.includes(pathParts[0])) {
      pathParts.shift();
    }
    // Reconstruct path with new language and preserve query parameters
    const newPath = `/${languageCode}${
      pathParts.length > 0 ? "/" + pathParts.join("/") : ""
    }${location.search}`;
    navigate(newPath, { replace: true });
  };

  const handleNavigation = (path: string) => {
    const currentLang = lang || i18n.language;
    // Ensure path starts with / but avoid double slashes
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    navigate(`/${currentLang}${cleanPath}`);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    const currentLang = lang || i18n.language;
    navigate(`/${currentLang}`);
  };

  const handleCartClick = () => {
    const currentLang = lang || i18n.language;
    if (location.pathname !== `/${currentLang}/checkout`) {
      setCartOpen(true);
    }
  };

  const isActive = (path: string) => {
    const currentLang = lang || i18n.language;
    const fullPath = `/${currentLang}${path}`;
    if (path === "/")
      return (
        location.pathname === fullPath ||
        location.pathname === `/${currentLang}`
      );
    return location.pathname.startsWith(fullPath);
  };

  // Handle scroll behavior to hide/show header - DISABLED to make header sticky
  if (autoHide)
    useEffect(() => {
      const handleScroll = () => {
        const currentScrollY = window.scrollY;

        // Only react to scroll if we've moved more than the threshold
        if (Math.abs(currentScrollY - lastScrollY) < scrollThreshold) {
          return;
        }

        // Show header when at the top
        if (currentScrollY < 10) {
          setIsVisible(true);
        } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
          // Hide when scrolling down and past 100px
          setIsVisible(false);
        } else if (currentScrollY < lastScrollY) {
          // Show when scrolling up
          setIsVisible(true);
        }

        setLastScrollY(currentScrollY);
      };

      // Throttle scroll events
      let timeoutId: NodeJS.Timeout | null = null;
      const throttledHandleScroll = () => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(handleScroll, 10);
      };

      window.addEventListener("scroll", throttledHandleScroll, {
        passive: true,
      });

      return () => {
        window.removeEventListener("scroll", throttledHandleScroll);
        if (timeoutId) clearTimeout(timeoutId);
      };
    }, [lastScrollY, scrollThreshold]);

  const NavItems = () => (
    <>
      <Button
        variant={isActive("/") ? "default" : "ghost"}
        size="sm"
        onClick={() => handleNavigation("/")}
        className={`justify-start rounded-full px-4 transition-all duration-300 ${
          isActive("/")
            ? "bg-primary text-primary-foreground shadow-lg"
            : "hover:bg-primary/10 hover:text-primary"
        }`}
      >
        {t("nav.home")}
      </Button>

      <Button
        variant={isActive("/products") ? "default" : "ghost"}
        size="sm"
        onClick={() => handleNavigation("/products")}
        className={`justify-start rounded-full px-4 transition-all duration-300 ${
          isActive("/products")
            ? "bg-primary text-primary-foreground shadow-lg"
            : "hover:bg-primary/10 hover:text-primary"
        }`}
      >
        {t("nav.products")}
      </Button>

      <Button
        variant={isActive("/discounts") ? "default" : "ghost"}
        size="sm"
        onClick={() => handleNavigation("/discounts")}
        className={`justify-start rounded-full px-4 transition-all duration-300 ${
          isActive("/discounts")
            ? "bg-primary text-primary-foreground shadow-lg"
            : "hover:bg-primary/10 hover:text-primary"
        }`}
      >
        {t("nav.discounts", "Deals")}
      </Button>

      <Button
        variant={isActive("/about") ? "default" : "ghost"}
        size="sm"
        onClick={() => handleNavigation("/about")}
        className={`justify-start rounded-full px-4 transition-all duration-300 ${
          isActive("/about")
            ? "bg-primary text-primary-foreground shadow-lg"
            : "hover:bg-primary/10 hover:text-primary"
        }`}
      >
        {t("nav.about", "About")}
      </Button>

      {/* Show Dashboard for all authenticated users */}
      {isAuthenticated && (
        <Button
          variant={isActive("/dashboard") ? "default" : "ghost"}
          size="sm"
          onClick={() => handleNavigation("/dashboard")}
          className={`justify-start rounded-full px-4 transition-all duration-300 ${
            isActive("/dashboard")
              ? "bg-primary text-primary-foreground shadow-lg"
              : "hover:bg-primary/10 hover:text-primary"
          }`}
        >
          {user?.role === "client" ? t("dashboard.orders") : t("nav.dashboard")}
        </Button>
      )}
    </>
  );

  return (
    <header
      className={`fixed top-0 z-50 w-full border-b bg-background/90 backdrop-blur-lg supports-[backdrop-filter]:bg-background/70 transition-transform duration-300 ease-in-out shadow-sm ${
        isVisible ? "translate-y-0" : "-translate-y-full"
      }`}
    >
      {/* Trust Bar */}
      <div className="hidden lg:block border-b border-border/60 bg-card/80">
        <div className="container mx-auto px-6 max-w-7xl h-10 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-6">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              {t("header.trust.certified", "Kosher products only")}
            </span>
            <span className="inline-flex items-center gap-2">
              <Truck className="h-4 w-4 text-primary" />
              {t("header.trust.delivery", "Same-day delivery in Cancun")}
            </span>
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              {t("header.trust.secure", "Secure checkout")}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <PhoneCall className="h-4 w-4 text-primary" />
            <a
              href="tel:+529985505030"
              className="hover:text-primary transition-colors"
              dir="ltr"
            >
              +52 998 550 5030
            </a>
          </div>
        </div>
      </div>

      <div className="container flex h-20 items-center justify-between px-4 md:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Logo */}
        <div className="relative p-2 rounded-xl">
          <img
            src={logoSvg}
            alt="Kosher Market Logo"
            width={2048}
            height={726}
            className="h-9 w-auto md:h-10 logo-blue-filter logo-glow transition-all duration-300"
          />
        </div>

        {/* Desktop Navigation - centered */}
        <div className="hidden md:flex items-center justify-center flex-1 max-w-3xl mx-8">
          <div className="flex items-center space-x-6 lg:space-x-8">
            <NavItems />
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="space-x-1 rounded-full border border-transparent hover:border-primary/20"
                >
                  <Globe size={16} />
                  <span>{currentLanguage.flag}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {LANGUAGES.map((lang) => {
                  const normalizedCurrentLang = i18n.language.split("-")[0];
                  const isSelected = normalizedCurrentLang === lang.code;
                  return (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`cursor-pointer ${
                        isSelected ? "bg-primary/10 font-semibold" : ""
                      }`}
                    >
                      <span className="mr-2">{lang.flag}</span>
                      {lang.name}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex items-center space-x-2 lg:space-x-3 flex-shrink-0">
          {/* Cart Button */}
          {!location.pathname.endsWith("/checkout") && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCartClick}
              className="relative rounded-full hover:bg-primary/10 transition-all duration-300 group"
              aria-label={t("nav.cart", "Cart")}
            >
              <ShoppingCart
                size={20}
                className="group-hover:text-primary transition-colors"
              />
              {itemCount > 0 && (
                <Badge className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center text-xs font-bold bg-primary text-primary-foreground shadow-lg animate-pulse">
                  {itemCount}
                </Badge>
              )}
            </Button>
          )}

          {/* User Menu */}
          {isAuthenticated ? (
            <div className="hidden md:flex items-center space-x-3">
              <span className="text-sm text-muted-foreground font-medium">
                {user?.name}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="hover:bg-destructive/10 hover:text-destructive"
              >
                {t("nav.logout")}
              </Button>
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                const currentLang = lang || i18n.language;
                navigate(`/${currentLang}/login`);
              }}
              className="hidden md:inline-flex rounded-full hover:bg-primary/10 hover:text-primary"
            >
              <User size={16} className="mr-2" />
              {t("nav.login")}
            </Button>
          )}

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                aria-label={t("nav.menu", "Open menu")}
              >
                <Menu size={20} />
              </Button>
            </SheetTrigger>
            <SheetContent
              side={currentLanguage.direction === "rtl" ? "right" : "left"}
              className="w-72"
            >
              <SheetHeader>
                <SheetTitle className="flex items-center justify-center space-x-2">
                  <img
                    src={logoSvg}
                    alt="Kosher Market Logo"
                    className="h-12 w-auto logo-blue-filter"
                  />
                </SheetTitle>
              </SheetHeader>
              <div className="mt-8 flex flex-col space-y-4 p-4">
                <NavItems />

                {/* Language Selector for Mobile */}
                <div className="border-t pt-4">
                  <div className="text-sm font-medium mb-2">
                    {t("nav.language")}
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {LANGUAGES.map((lang) => {
                      const normalizedCurrentLang = i18n.language.split("-")[0];
                      const isSelected = normalizedCurrentLang === lang.code;
                      return (
                        <Button
                          key={lang.code}
                          variant={isSelected ? "default" : "ghost"}
                          size="sm"
                          onClick={() => changeLanguage(lang.code)}
                          className="justify-start"
                        >
                          <span className="mr-2">{lang.flag}</span>
                          {lang.name}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                {/* Theme Toggle Mobile - removed: follows system */}

                <div className="border-t pt-4">
                  {isAuthenticated ? (
                    <div className="space-y-2">
                      <div className="text-sm text-muted-foreground px-3">
                        {user?.name}
                      </div>
                      <Button
                        variant="ghost"
                        onClick={handleLogout}
                        className="w-full justify-start"
                      >
                        {t("nav.logout")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        const currentLang = lang || i18n.language;
                        navigate(`/${currentLang}/login`);
                        setMobileMenuOpen(false);
                      }}
                      className="w-full justify-start"
                    >
                      <User size={16} className="mr-2" />
                      {t("nav.login")}
                    </Button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
